import React, { useState, useEffect, useRef } from 'react';
import Plot from 'react-plotly.js';

const LiteratureDiscoveryGraphComponent = ({ graphData, isLoading, contentId, subtitle }) => {
  const plotRef = useRef(null);
  const [isDarkMode, setIsDarkMode] = useState(
    document.documentElement.getAttribute('data-theme') === 'dark'
  );
  const [zoomLevel, setZoomLevel] = useState(1);
  
  const getCSSVariable = (variable) => {
    return getComputedStyle(document.documentElement).getPropertyValue(variable).trim();
  };

  const getFontSize = (className) => {
    const tempElement = document.createElement('span');
    tempElement.className = className;
    tempElement.style.visibility = 'hidden';
    document.body.appendChild(tempElement);
    const fontSize = parseFloat(getComputedStyle(tempElement).fontSize);
    document.body.removeChild(tempElement);
    return fontSize;
  };

  const getMargin = (className) => {
    const tempElement = document.createElement('span');
    tempElement.className = className;
    tempElement.style.visibility = 'hidden';
    document.body.appendChild(tempElement);
    const margin = parseFloat(getComputedStyle(tempElement).marginLeft);
    document.body.removeChild(tempElement);
    return margin;
  };
  
  const getFontWeight = (className) => {
    const tempElement = document.createElement('span');
    tempElement.className = className;
    tempElement.style.visibility = 'hidden';
    document.body.appendChild(tempElement);
    const fontWeight = getComputedStyle(tempElement).fontWeight;
    document.body.removeChild(tempElement);
    return fontWeight;
  };

  const getFontFamily = (className) => {
    const tempElement = document.createElement('span');
    tempElement.className = className;
    tempElement.style.visibility = 'hidden';
    document.body.appendChild(tempElement);
    const fontFamily = getComputedStyle(tempElement).fontFamily;
    document.body.removeChild(tempElement);
    return fontFamily;
  };

  useEffect(() => {
    const observer = new MutationObserver((mutations) => {
      mutations.forEach((mutation) => {
        if (mutation.type === 'attributes' && mutation.attributeName === 'data-theme') {
          const newTheme = document.documentElement.getAttribute('data-theme');
          setIsDarkMode(newTheme === 'dark');
        }
      });
    });

    observer.observe(document.documentElement, {
      attributes: true,
      attributeFilter: ['data-theme']
    });

    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    return () => {
      if (plotRef.current && plotRef.current.el) {
        try {
          window.Plotly?.purge(plotRef.current.el);
        } catch (error) {
          console.debug('Plotly cleanup handled:', error.message);
        }
      }
    };
  }, []);

  useEffect(() => {
    const handleError = (event) => {
      if (event.error?.message?.includes('selectAll') || 
          event.error?.stack?.includes('plotly')) {
        event.preventDefault();
        event.stopPropagation();
        return false;
      }
    };
    
    window.addEventListener('error', handleError);
    window.addEventListener('unhandledrejection', handleError);
    
    return () => {
      window.removeEventListener('error', handleError);
      window.removeEventListener('unhandledrejection', handleError);
    };
  }, []);

  useEffect(() => {
    const styleId = 'plotly-modebar-styles';
    
    if (!document.getElementById(styleId)) {
      const style = document.createElement('style');
      style.id = styleId;
      style.textContent = `
        .modebar-btn {
          width: var(--px-30) !important;
          height: var(--px-30) !important; 
          padding: var(--px-5) !important;
        }
        .modebar-btn svg {
          width: var(--px-16) !important;
          height: var(--px-16) !important;
        }
        /* Modebar button tooltip text styling */
        [data-title]:before,
        [data-title]:after {
          font-size: var(--px-11) !important;
          line-height: 1.4 !important;
        }
        /* Hover tooltip styling */
        .hoverlayer .hovertext path {
          stroke: var(--color8) !important;
        }
        .hoverlayer .hovertext text {
          font-size: var(--px-11) !important;
          font-family: var(--poppinsfont) !important;
        }
        g.hovertext text {
          font-size: var(--px-11) !important;
          font-family: var(--poppinsfont) !important;
        }
      `;
      document.head.appendChild(style);
    }
    
    return () => {
      const styleElement = document.getElementById(styleId);
      if (styleElement) {
        document.head.removeChild(styleElement);
      }
    };
  }, []);

  useEffect(() => {
    const handleResize = () => {
      setZoomLevel(window.devicePixelRatio || 1);
    };

    window.addEventListener('resize', handleResize);
    window.visualViewport?.addEventListener('resize', handleResize);
    
    return () => {
      window.removeEventListener('resize', handleResize);
      window.visualViewport?.removeEventListener('resize', handleResize);
    };
  }, []);

  if (isLoading) {
    return (
      <div className="graph-container align-items-center justify-content-center d-flex ">
        <div className="text-center">
          <div className="spinner-border text-primary" role="status">
            <span className="visually-hidden">Loading...</span>
          </div>
          <br />
          <span className="ev-reg-p-dark">
            Generating Plot
          </span>
        </div>
      </div>
    );
  }

  if (!graphData || !graphData.figure_dict) {
    return (
      <div className="container-error">
        <div className="text-center">
          <p>Error loading graph</p>
        </div>
      </div>
    );
  }

  const themeColors = {
    paper_bgcolor: getCSSVariable('--background2'),
    plot_bgcolor: getCSSVariable('--background2'),
    fontColor: getCSSVariable('--color3'),  
    labelColor: isDarkMode ? getCSSVariable('--color17') : getCSSVariable('--color3'),
    gridColor: getCSSVariable('--color12'),
    titleColor: getCSSVariable('--color2'),
  };

  const fontSize = getFontSize('ev-fs-11');
  const axisSize = getFontSize('ev-fs-11');
  const mainTitleSize = getFontSize('ev-fs-13');

  const mainTitleWeight = getFontWeight('ev-bold-med');
  const axisTitleWeight = getFontWeight('ev-bold-med');

  const marginLeft = getMargin('margin-left-60');
  const marginRight = getMargin('margin-right-60');
  const marginBottom = getMargin('margin-bottom-100');

  const fontFamily = getFontFamily('poppinsfont');

  const getAxisConfig = (axisName) => {
    const axisData = graphData.figure_dict.layout?.[axisName];
    if (!axisData) return undefined;

    const isYAxis = axisName.startsWith('y');
    
    return {
      ...axisData,
      gridcolor: themeColors.gridColor,
      linecolor: themeColors.fontColor,
      tickcolor: themeColors.fontColor,
      automargin: true,
      ...(isYAxis && { fixedrange: false }),
      ...(!isYAxis && { tickangle: -45 }),
      title: {
        ...axisData?.title,
        font: {
          ...axisData?.title?.font,
          family: fontFamily,
          color: themeColors.fontColor,
          size: axisSize,
          weight: axisTitleWeight
        }
      },
      tickfont: { 
        ...axisData?.tickfont,
        family: fontFamily,
        color: themeColors.fontColor,
        size: fontSize
      }
    };
  };

  const dynamicAxes = {};
  Object.keys(graphData.figure_dict.layout || {}).forEach(key => {
    if (key.match(/^[xy]axis\d*$/)) {  
      const axisConfig = getAxisConfig(key);
      if (axisConfig) {
        dynamicAxes[key] = axisConfig;
      }
    }
  });

  const getAnnotationConfig = () => {
    const annotations = graphData.figure_dict.layout?.annotations;
    if (!annotations || annotations.length === 0) return undefined;

    return annotations.map(annotation => ({
      ...annotation,
      font: {
        ...annotation.font,
        family: fontFamily,
        color: themeColors.labelColor,
        size: annotation.font?.size ?? fontSize,
        weight: axisTitleWeight
      },
      bgcolor:      annotation.bgcolor
                    ? (isDarkMode ? 'rgba(30,41,59,0.9)' : 'rgba(241,245,249,0.95)')
                    : undefined,
      bordercolor:  annotation.bordercolor
                    ? (isDarkMode ? '#475569' : '#94a3b8')
                    : undefined,
      yanchor: annotation.yanchor || 'bottom',
      xanchor: annotation.xanchor || 'center'
    }));
  };

  const dynamicAnnotations = getAnnotationConfig();

  return (
    <div className="graph-container ps-0 pe-4">
      {subtitle && ( 
        <div className="mb-2">
          <span className="ev-message-card2 w-100 m-0 ev-fs-11">
            <i className="bi bi-bar-chart-fill"></i> <b>Figure {contentId}. </b> {subtitle}
          </span>
        </div>
      )}
      <Plot
        data={graphData.figure_dict.data?.map(trace => {
            if ((trace.type === 'pie' || trace.type === 'bar') && trace.labels) {
                return {
                    ...trace,
                    labels: trace.labels.map(l =>
                        typeof l === 'string'
                            ? l.replace(/_/g, ' ').toLowerCase().replace(/^\w/, c => c.toUpperCase())
                            : l
                    )
                };
            }
            return trace;
        })}
        ref={plotRef}
        layout={{
          ...graphData.figure_dict.layout,

          title: graphData.figure_dict.layout?.title
        ? {
            ...graphData.figure_dict.layout.title,
            font: {
              ...graphData.figure_dict.layout.title.font,
              family: fontFamily,
              color: themeColors.titleColor,
              size: mainTitleSize,
              weight: mainTitleWeight
            },
            y: 0.98,
            yref: 'paper',
            yanchor: 'top',
            x: 0.5,
            xanchor: 'center'
          }
        : undefined,
          
          paper_bgcolor: themeColors.paper_bgcolor,
          plot_bgcolor: themeColors.plot_bgcolor,
          font: {
            ...graphData.figure_dict.layout?.font,
            family: fontFamily,
            color: themeColors.labelColor,
            size: fontSize
          },

          hoverlabel: {
            ...graphData.figure_dict.layout?.hoverlabel,
            bgcolor: themeColors.paper_bgcolor,
            bordercolor: themeColors.gridColor,
            font: {
              family: fontFamily,
              size: fontSize,
              color: getCSSVariable('--color4'),
            }
          },

          scene: {
            ...graphData.figure_dict.layout?.scene,
            bgcolor: themeColors.plot_bgcolor,
            xaxis: {
              ...graphData.figure_dict.layout?.scene?.xaxis,
              backgroundcolor: themeColors.plot_bgcolor,
              gridcolor: themeColors.gridColor,
              linecolor: themeColors.fontColor,
              tickcolor: themeColors.fontColor,
              title: {
                ...graphData.figure_dict.layout?.scene?.xaxis?.title,
                font: { 
                  family: fontFamily,
                  color: themeColors.fontColor, 
                  size: axisSize,
                  weight: axisTitleWeight
                }
              },
              tickfont: { 
                family: fontFamily,
                color: themeColors.fontColor, 
                size: fontSize 
              }
            },
            yaxis: {
              ...graphData.figure_dict.layout?.scene?.yaxis,
              backgroundcolor: themeColors.plot_bgcolor,
              gridcolor: themeColors.gridColor,
              linecolor: themeColors.fontColor,
              tickcolor: themeColors.fontColor,
              title: {
                ...graphData.figure_dict.layout?.scene?.yaxis?.title,
                font: { 
                  family: fontFamily,
                  color: themeColors.fontColor, 
                  size: axisSize,
                  weight: axisTitleWeight
                }
              },
              tickfont: { 
                family: fontFamily,
                color: themeColors.fontColor, 
                size: fontSize 
              }
            },
            zaxis: {
              ...graphData.figure_dict.layout?.scene?.zaxis,
              backgroundcolor: themeColors.plot_bgcolor,
              gridcolor: themeColors.gridColor,
              linecolor: themeColors.fontColor,
              tickcolor: themeColors.fontColor,
              title: {
                ...graphData.figure_dict.layout?.scene?.zaxis?.title,
                font: { 
                  family: fontFamily,
                  color: themeColors.fontColor, 
                  size: axisSize,
                  weight: axisTitleWeight
                }
              },
              tickfont: { 
                family: fontFamily,
                color: themeColors.fontColor, 
                size: fontSize 
              }
            }
          },

          polar: {
            ...graphData.figure_dict.layout?.polar,
            bgcolor: themeColors.plot_bgcolor,
            radialaxis: {
              ...graphData.figure_dict.layout?.polar?.radialaxis,
              gridcolor: themeColors.gridColor,
              linecolor: themeColors.fontColor,
              tickcolor: themeColors.fontColor,
              tickfont: { 
                family: fontFamily,
                color: themeColors.fontColor, 
                size: fontSize 
              },
              title: {
                ...graphData.figure_dict.layout?.polar?.radialaxis?.title,
                font: { 
                  family: fontFamily,
                  color: themeColors.fontColor, 
                  size: axisSize,
                  weight: axisTitleWeight
                }
              }
            },
            angularaxis: {
              ...graphData.figure_dict.layout?.polar?.angularaxis,
              gridcolor: themeColors.gridColor,
              linecolor: themeColors.fontColor,
              tickcolor: themeColors.fontColor,
              tickfont: { 
                family: fontFamily,
                color: themeColors.fontColor, 
                size: fontSize 
              }
            }
          },

          geo: {
            ...graphData.figure_dict.layout?.geo,
            bgcolor: 'rgba(0,0,0,0)',
            lakecolor:      isDarkMode ? '#0d1b2e'          : 'rgb(204,229,255)',
            landcolor:      isDarkMode ? '#1e293b'          : 'rgb(243,243,243)',
            oceancolor:     isDarkMode ? '#0d1b2e'          : 'rgb(204,229,255)',
            countrycolor:   isDarkMode ? '#475569'          : '#adb5bd',
            subunitcolor:   isDarkMode ? '#475569'          : '#adb5bd',
            coastlinecolor: isDarkMode ? '#475569'          : '#adb5bd',
            framecolor:     'rgba(0,0,0,0)',
            projection: {
              ...graphData.figure_dict.layout?.geo?.projection
            }
          },

          ternary: {
            ...graphData.figure_dict.layout?.ternary,
            bgcolor: themeColors.plot_bgcolor,
            aaxis: {
              ...graphData.figure_dict.layout?.ternary?.aaxis,
              gridcolor: themeColors.gridColor,
              linecolor: themeColors.fontColor,
              tickcolor: themeColors.fontColor,
              tickfont: { 
                family: fontFamily,
                color: themeColors.fontColor, 
                size: fontSize 
              }
            },
            baxis: {
              ...graphData.figure_dict.layout?.ternary?.baxis,
              gridcolor: themeColors.gridColor,
              linecolor: themeColors.fontColor,
              tickcolor: themeColors.fontColor,
              tickfont: { 
                family: fontFamily,
                color: themeColors.fontColor, 
                size: fontSize 
              }
            },
            caxis: {
              ...graphData.figure_dict.layout?.ternary?.caxis,
              gridcolor: themeColors.gridColor,
              linecolor: themeColors.fontColor,
              tickcolor: themeColors.fontColor,
              tickfont: { 
                family: fontFamily,
                color: themeColors.fontColor, 
                size: fontSize 
              }
            }
          },
          
          autosize: true,
          margin: { 
            l: graphData.figure_dict.layout?.margin?.l ?? marginLeft,
            r: graphData.figure_dict.layout?.margin?.r ?? marginRight,
            t: 55,
            b: graphData.figure_dict.layout?.margin?.b ?? marginBottom,
          },  

          ...dynamicAxes,

          ...(dynamicAnnotations && { annotations: dynamicAnnotations }),

          legend: {
            ...graphData.figure_dict.layout?.legend,
            bgcolor: themeColors.paper_bgcolor,
            bordercolor: themeColors.gridColor,
            font: { 
              family: fontFamily,
              color: themeColors.labelColor,
              size: fontSize 
            },
            orientation: 'v',
            x: 0.99,
            y: 1,
            xanchor: 'right',
            yanchor: 'top'
          },
           
          width: undefined,
          height: undefined
        }}
        config={{
          responsive: true,
          displayModeBar: true,
          displaylogo: false,
          modeBarButtonsToRemove: ['pan2d', 'lasso2d', 'select2d'],
          doubleClick: 'reset',
          showTips: false
        }}
        className="w-100"
        useResizeHandler={false}
        style={{ width: "100%", height: `${graphData.figure_dict.layout?.height || 500}px` }}
        key={`${isDarkMode ? 'dark' : 'light'}-${zoomLevel}`}
      />
    </div>
  );
};

export default LiteratureDiscoveryGraphComponent;